import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Archive,
  CheckCircle2,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  Search,
  X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  api,
  type CarrierInitializationAccountSummary,
  type CarrierInitializationCarrierReviewStatus,
  type CarrierAuditState,
  type CarrierFolderPublicationProgress,
  type CarrierInitializationIgnoredRecord,
  type CarrierInitializationMappingDrafts,
  type CarrierInitializationVendorAssignmentSource,
  type CarrierInitializationVendorSummary,
  type CarrierRecordIgnoreReason,
  type CarrierRecordType,
} from '@/services/api';
import { useChatStore } from '@/stores/chatStore';
import {
  carrierAuditActionState,
  carrierFolderPublicationBlockedMessage,
  carrierFolderPreview,
  carrierOrganizerSetupMessage,
  buildCarrierMappingTableRows,
  carrierProposalPlan,
  createDefaultVendorAssignments,
  PROPOSED_NEW_CARRIER_PREFIX,
  type CarrierMappingTableRow,
  proposedVendorTargets,
  ignoredRecordLabel,
  validateCarrierNames,
} from './carrierMappingDraftState';
import { CarrierTargetPicker } from './CarrierTargetPicker';
import { CarrierMappingTable } from './CarrierMappingTable';
import { runCarrierFolderPublication } from './carrierFolderPublicationRunner';
import { carrierTrafficHistoryEffectiveDateState } from './carrierTrafficHistoryEffectiveDateState';

// One follower per audit run, shared by every Organizer surface in the chat.
const followedAuditRuns = new Set<string>();

interface LocalCarrierDraft {
  key: string;
  id?: string;
  sourceCompanyId?: number;
}

type CarrierTarget = `company:${number}` | `carrier:${string}`;
type OrganizerView = 'attention' | 'all' | 'accounts' | 'vendors' | 'ignored';

interface IgnoreTarget {
  recordType: CarrierRecordType;
  providerRecordId: number;
  name: string;
}

const IGNORE_REASONS: Array<{ value: CarrierRecordIgnoreReason; label: string }> = [
  { value: 'decommissioned', label: 'Decommissioned' },
  { value: 'test_or_internal', label: 'Test or internal record' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'not_a_carrier', label: 'Not a real Carrier' },
  { value: 'other', label: 'Other' },
];

function companyTarget(providerCompanyId: number): CarrierTarget {
  return `company:${providerCompanyId}`;
}

function carrierTarget(key: string): CarrierTarget {
  return `carrier:${key}`;
}

function localDraftTarget(draft: LocalCarrierDraft): CarrierTarget {
  return draft.sourceCompanyId === undefined
    ? carrierTarget(draft.key)
    : companyTarget(draft.sourceCompanyId);
}

function ignoredKey(record: CarrierInitializationIgnoredRecord): string {
  return `${record.recordType}:${record.providerRecordId}`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'The Carrier setup could not be saved.';
}

function blockedMessage(
  reason: CarrierInitializationMappingDrafts['blockedReason']
): string {
  if (reason === 'source_truncated') {
    return 'Some discovery results are missing. Finish finding carrier data before you organize Carriers.';
  }
  if (reason === 'group_limit') {
    return 'This Carrier list is too large for the current review.';
  }
  return 'Finish finding carrier data before you organize Carriers.';
}

interface OrganizerVendor extends CarrierInitializationVendorSummary {
  suggestedProviderCompanyId: number | null;
  proposedProviderCompanyId?: number;
  proposedCarrierDraftId?: string;
  proposedNewCarrierName?: string;
  proposalReason?: string;
  assignmentSource: CarrierInitializationVendorAssignmentSource | null;
}

function uniqueVendors(mapping: CarrierInitializationMappingDrafts): OrganizerVendor[] {
  const vendors = new Map<number, OrganizerVendor>();
  for (const vendor of mapping.unassignedVendors) {
    vendors.set(vendor.providerCarrierId, {
      ...vendor,
      assignmentSource: null,
    });
  }
  for (const draft of mapping.drafts) {
    for (const vendor of draft.assignedVendors) {
      vendors.set(vendor.providerCarrierId, {
        ...vendor,
        suggestedProviderCompanyId: null,
      });
    }
  }
  return [...vendors.values()].sort((left, right) => (
    left.name.localeCompare(right.name) || left.providerCarrierId - right.providerCarrierId
  ));
}

function uniqueAccounts(mapping: CarrierInitializationMappingDrafts) {
  const accounts = new Map<number, CarrierInitializationAccountSummary>();
  for (const group of mapping.groups) {
    for (const account of group.accounts) accounts.set(account.providerAccountId, account);
  }
  for (const draft of mapping.drafts) {
    for (const account of draft.assignedAccounts) accounts.set(account.providerAccountId, account);
  }
  for (const account of mapping.unassignedAccounts) {
    accounts.set(account.providerAccountId, account);
  }
  return [...accounts.values()].sort((left, right) => (
    left.name.localeCompare(right.name) || left.providerAccountId - right.providerAccountId
  ));
}

function reasonLabel(reason: CarrierRecordIgnoreReason): string {
  return IGNORE_REASONS.find((option) => option.value === reason)?.label ?? reason;
}

export function CarrierOrganizerMissingMapping({ error }: { error: string | null }) {
  const setupMessage = carrierOrganizerSetupMessage({ mapping: null, error });
  if (setupMessage) {
    return (
      <p role="status" className="mt-3 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {setupMessage}
      </p>
    );
  }
  return (
    <p className="mt-3 flex items-start gap-2 text-xs text-red-700 dark:text-red-300">
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      {error}
    </p>
  );
}

export function CarrierMappingDrafts({
  disabled = false,
  defaultOpen = false,
  defaultOpenKey,
}: {
  disabled?: boolean;
  defaultOpen?: boolean;
  defaultOpenKey?: string;
}) {
  const [mapping, setMapping] = useState<CarrierInitializationMappingDrafts | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [localDrafts, setLocalDrafts] = useState<LocalCarrierDraft[]>([]);
  const [accountAssignments, setAccountAssignments] = useState<Record<number, CarrierTarget>>({});
  const [vendorAssignments, setVendorAssignments] = useState<Record<number, CarrierTarget>>({});
  const [accountServiceDefinitionIds, setAccountServiceDefinitionIds] = useState<Record<number, string>>({});
  const [vendorServiceDefinitionIds, setVendorServiceDefinitionIds] = useState<Record<number, string>>({});
  const [inferredVendorIds, setInferredVendorIds] = useState<Set<number>>(new Set());
  const [reviewStatuses, setReviewStatuses] = useState<Record<CarrierTarget, CarrierInitializationCarrierReviewStatus>>({});
  const [reviewNowTargets, setReviewNowTargets] = useState<Set<CarrierTarget>>(new Set());
  const [accounts, setAccounts] = useState<CarrierInitializationAccountSummary[]>([]);
  const [vendors, setVendors] = useState<ReturnType<typeof uniqueVendors>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishingFolders, setPublishingFolders] = useState(false);
  const [savingEffectiveDate, setSavingEffectiveDate] = useState(false);
  const [folderPublication, setFolderPublication] = useState<CarrierFolderPublicationProgress | null>(null);
  const [trafficHistoryEffectiveDate, setTrafficHistoryEffectiveDate] = useState('');
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizerOpen, setOrganizerOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const [view, setView] = useState<OrganizerView>('all');
  const [query, setQuery] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState<CarrierTarget | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [selectedIgnoredKey, setSelectedIgnoredKey] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerSelection, setPickerSelection] = useState<Set<number>>(new Set());
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);
  const [accountPickerQuery, setAccountPickerQuery] = useState('');
  const [accountPickerSelection, setAccountPickerSelection] = useState<Set<number>>(new Set());
  const [ignoreTarget, setIgnoreTarget] = useState<IgnoreTarget | null>(null);
  const [ignoreReason, setIgnoreReason] = useState<CarrierRecordIgnoreReason | ''>('');
  const [audit, setAudit] = useState<CarrierAuditState | null>(null);
  const [auditStarting, setAuditStarting] = useState(false);
  const activeSessionId = useChatStore((state) => state.activeSessionId);
  const refreshSession = useChatStore((state) => state.refreshSession);
  const folderPublicationActive = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    // StrictMode replays effects, so set the flag on every setup.
    mountedRef.current = true;
    return () => {
      folderPublicationActive.current = false;
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!defaultOpen) return;
    if (!defaultOpenKey || typeof sessionStorage === 'undefined') {
      setOrganizerOpen(true);
      setTableOpen(true);
      return;
    }
    const storageKey = `telcoos:carrier-organizer-opened:${defaultOpenKey}`;
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, '1');
    setOrganizerOpen(true);
    // The full-screen table is the main review surface; Close returns to the panel.
    setTableOpen(true);
  }, [defaultOpen, defaultOpenKey]);

  const applyMapping = useCallback((result: CarrierInitializationMappingDrafts | null) => {
    setMapping(result);
    if (!result) {
      setDirty(false);
      setNames({});
      setLocalDrafts([]);
      setAccountAssignments({});
      setVendorAssignments({});
      setAccountServiceDefinitionIds({});
      setVendorServiceDefinitionIds({});
      setInferredVendorIds(new Set());
      setReviewStatuses({});
      setReviewNowTargets(new Set());
      setAccounts([]);
      setVendors([]);
      setSelectedCarrier(null);
      return;
    }
    const nextNames: Record<string, string> = {};
    const nextAccountAssignments: Record<number, CarrierTarget> = {};
    const nextVendorAssignments: Record<number, CarrierTarget> = {};
    const nextReviewStatuses: Record<CarrierTarget, CarrierInitializationCarrierReviewStatus> = {};
    const nextDrafts: LocalCarrierDraft[] = [];
    if (result.drafts.length > 0) {
      for (const draft of result.drafts) {
        const key = carrierTarget(draft.id);
        nextDrafts.push({ key: draft.id, id: draft.id });
        nextNames[key] = draft.name;
        nextReviewStatuses[key] = draft.reviewStatus;
        for (const account of draft.assignedAccounts) {
          nextAccountAssignments[account.providerAccountId] = key;
        }
        for (const vendor of draft.assignedVendors) {
          nextVendorAssignments[vendor.providerCarrierId] = key;
        }
      }
    } else {
      for (const group of result.groups) {
        const target = companyTarget(group.providerCompanyId);
        nextDrafts.push({ key: String(group.providerCompanyId), sourceCompanyId: group.providerCompanyId });
        nextNames[target] = group.providerCompanyName;
        nextReviewStatuses[target] = 'not_required';
        for (const account of group.accounts) {
          nextAccountAssignments[account.providerAccountId] = target;
        }
      }
    }
    const defaultVendorAssignments = createDefaultVendorAssignments(result);
    for (const [vendorId, target] of Object.entries(defaultVendorAssignments)) {
      nextVendorAssignments[Number(vendorId)] = target as CarrierTarget;
      nextReviewStatuses[target as CarrierTarget] = 'needs_review';
    }
    const nextInferredVendorIds = new Set(
      Object.keys(defaultVendorAssignments).map(Number)
    );
    const nextAccounts = uniqueAccounts(result);
    const nextVendors = uniqueVendors(result);
    setNames(nextNames);
    setLocalDrafts(nextDrafts);
    setAccountAssignments(nextAccountAssignments);
    setVendorAssignments(nextVendorAssignments);
    setAccountServiceDefinitionIds(Object.fromEntries(nextAccounts.flatMap((account) => (
      account.serviceDefinitionId
        ? [[account.providerAccountId, account.serviceDefinitionId]]
        : []
    ))));
    setVendorServiceDefinitionIds(Object.fromEntries(nextVendors.flatMap((vendor) => (
      vendor.serviceDefinitionId
        ? [[vendor.providerCarrierId, vendor.serviceDefinitionId]]
        : []
    ))));
    setInferredVendorIds(nextInferredVendorIds);
    setReviewStatuses(nextReviewStatuses);
    setReviewNowTargets(new Set());
    setDirty(nextInferredVendorIds.size > 0);
    setAccounts(nextAccounts);
    setVendors(nextVendors);
    setSelectedCarrier((current) => {
      const validTargets = new Set(nextDrafts.map((draft) => (
        draft.sourceCompanyId === undefined
          ? carrierTarget(draft.key)
          : companyTarget(draft.sourceCompanyId)
      )));
      return current && validTargets.has(current)
        ? current
        : [...validTargets][0] ?? null;
    });
    setSelectedAccountId((current) => (
      current && result.unassignedAccounts.some((account) => account.providerAccountId === current)
        ? current
        : result.unassignedAccounts[0]?.providerAccountId ?? null
    ));
    const remainingUnassignedVendors = result.unassignedVendors.filter(
      (vendor) => !nextInferredVendorIds.has(vendor.providerCarrierId)
    );
    setSelectedVendorId((current) => (
      current && remainingUnassignedVendors.some(
        (vendor) => vendor.providerCarrierId === current
      )
        ? current
        : remainingUnassignedVendors[0]?.providerCarrierId ?? null
    ));
    setSelectedIgnoredKey((current) => (
      current && result.ignoredRecords.some((record) => ignoredKey(record) === current)
        ? current
        : result.ignoredRecords[0]
          ? ignoredKey(result.ignoredRecords[0])
          : null
    ));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mappingResult, folderPublicationResult, auditResult] = await Promise.all([
        api.carrierInitialization.getMappingDrafts(),
        api.carrierInitialization.getFolderPublication(),
        api.carrierInitialization.getCarrierAudit(),
      ]);
      applyMapping(mappingResult);
      setFolderPublication(folderPublicationResult);
      setAudit(auditResult);
      setTrafficHistoryEffectiveDate(
        folderPublicationResult?.trafficHistoryEffectiveDate.providerCalendarDate ?? ''
      );
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [applyMapping]);

  useEffect(() => {
    void load();
  }, [load]);

  // Waits for the background audit worker, then reloads the owning chat so the
  // worker's result or pause message appears there.
  const followAudit = useCallback(async (chatSessionId: string, runId: string) => {
    if (followedAuditRuns.has(runId)) return;
    followedAuditRuns.add(runId);
    try {
      for (let attempt = 0; attempt < 360 && mountedRef.current; attempt += 1) {
        const current = await api.carrierInitialization.getCarrierAudit();
        if (!mountedRef.current) return;
        setAudit(current);
        if (current?.status !== 'running' || current.dispatch?.status !== 'running') break;
        await new Promise((resolve) => setTimeout(resolve, 5_000));
      }
      if (mountedRef.current) {
        await refreshSession(chatSessionId);
        await load();
      }
    } catch (auditError) {
      if (mountedRef.current) setError(errorMessage(auditError));
    } finally {
      followedAuditRuns.delete(runId);
    }
  }, [load, refreshSession]);

  // A reopened Organizer keeps following an audit that runs for this chat.
  useEffect(() => {
    if (
      audit?.status === 'running'
      && audit.dispatch?.status === 'running'
      && audit.dispatch.chatSessionId === activeSessionId
    ) {
      void followAudit(audit.dispatch.chatSessionId, audit.runId);
    }
  }, [activeSessionId, audit, followAudit]);

  const unassignedVendors = useMemo(
    () => vendors.filter((vendor) => vendorAssignments[vendor.providerCarrierId] === undefined),
    [vendorAssignments, vendors]
  );
  const unassignedAccounts = useMemo(
    () => accounts.filter(
      (account) => accountAssignments[account.providerAccountId] === undefined
    ),
    [accountAssignments, accounts]
  );

  const markDirty = () => {
    setDirty(true);
    setSaved(false);
    setError(null);
  };

  const requireCarrierReview = (...targets: Array<CarrierTarget | undefined>) => {
    const activeTargets = targets.filter((target): target is CarrierTarget => Boolean(target));
    if (activeTargets.length === 0) return;
    setReviewStatuses((current) => {
      const next = { ...current };
      for (const target of activeTargets) next[target] = 'needs_review';
      return next;
    });
    setReviewNowTargets((current) => {
      const next = new Set(current);
      for (const target of activeTargets) next.delete(target);
      return next;
    });
  };

  const removeEmptyDraft = (
    target: CarrierTarget,
    excludedAccountId?: number,
    excludedVendorId?: number
  ) => {
    const hasAccounts = Object.entries(accountAssignments).some(
      ([accountId, assignedTarget]) => Number(accountId) !== excludedAccountId
        && assignedTarget === target
    );
    const hasVendors = Object.entries(vendorAssignments).some(
      ([vendorId, assignedTarget]) => Number(vendorId) !== excludedVendorId
        && assignedTarget === target
    );
    if (hasAccounts || hasVendors) return;
    setLocalDrafts((current) => current.filter(
      (draft) => localDraftTarget(draft) !== target
    ));
    setReviewStatuses((current) => {
      const next = { ...current };
      delete next[target];
      return next;
    });
    setReviewNowTargets((current) => {
      const next = new Set(current);
      next.delete(target);
      return next;
    });
    setNames((current) => {
      const next = { ...current };
      delete next[target];
      return next;
    });
    if (selectedCarrier === target) {
      setSelectedCarrier(localDrafts
        .map(localDraftTarget)
        .find((candidate) => candidate !== target) ?? null);
    }
  };

  const assignAccount = (
    account: CarrierInitializationAccountSummary,
    target: string
  ) => {
    if (!target) return;
    const previousTarget = accountAssignments[account.providerAccountId];
    let resolvedTarget = target as CarrierTarget;
    if (target === 'new') {
      const key = `new-account-${account.providerAccountId}`;
      resolvedTarget = carrierTarget(key);
      setLocalDrafts((current) => (
        current.some((draft) => draft.key === key)
          ? current
          : [...current, { key }]
      ));
      setNames((current) => ({
        ...current,
        [resolvedTarget]: account.company.name,
      }));
      setReviewStatuses((current) => ({
        ...current,
        [resolvedTarget]: 'needs_review',
      }));
    }
    setAccountAssignments((current) => ({
      ...current,
      [account.providerAccountId]: resolvedTarget,
    }));
    setSelectedCarrier(resolvedTarget);
    if (selectedAccountId === account.providerAccountId) {
      setSelectedAccountId(
        unassignedAccounts.find(
          (candidate) => candidate.providerAccountId !== account.providerAccountId
        )?.providerAccountId ?? null
      );
    }
    requireCarrierReview(resolvedTarget, previousTarget);
    if (previousTarget && previousTarget !== resolvedTarget) {
      removeEmptyDraft(previousTarget, account.providerAccountId);
    }
    markDirty();
  };

  const removeAccount = (accountId: number) => {
    const target = accountAssignments[accountId];
    setAccountAssignments((current) => {
      const next = { ...current };
      delete next[accountId];
      return next;
    });
    requireCarrierReview(target);
    if (target) removeEmptyDraft(target, accountId);
    setSelectedAccountId(accountId);
    markDirty();
  };

  const assignVendor = (vendor: (typeof vendors)[number], target: string) => {
    if (!target) return;
    const previousTarget = vendorAssignments[vendor.providerCarrierId];
    let resolvedTarget = target as CarrierTarget;
    if (target === 'new') {
      const key = `new-vendor-${vendor.providerCarrierId}`;
      resolvedTarget = carrierTarget(key);
      setLocalDrafts((current) => (
        current.some((draft) => draft.key === key)
          ? current
          : [...current, { key }]
      ));
      setNames((current) => ({ ...current, [resolvedTarget]: vendor.name }));
      setReviewStatuses((current) => ({
        ...current,
        [resolvedTarget]: 'needs_review',
      }));
      setSelectedCarrier(resolvedTarget);
    }
    setVendorAssignments((current) => ({
      ...current,
      [vendor.providerCarrierId]: resolvedTarget,
    }));
    setInferredVendorIds((current) => {
      const next = new Set(current);
      next.delete(vendor.providerCarrierId);
      return next;
    });
    if (selectedVendorId === vendor.providerCarrierId) {
      setSelectedVendorId(
        unassignedVendors.find(
          (candidate) => candidate.providerCarrierId !== vendor.providerCarrierId
        )?.providerCarrierId ?? null
      );
    }
    requireCarrierReview(resolvedTarget, previousTarget);
    if (previousTarget && previousTarget !== resolvedTarget) {
      removeEmptyDraft(previousTarget, undefined, vendor.providerCarrierId);
    }
    markDirty();
  };

  const removeVendor = (vendorId: number) => {
    const target = vendorAssignments[vendorId];
    setVendorAssignments((current) => {
      const next = { ...current };
      delete next[vendorId];
      return next;
    });
    setInferredVendorIds((current) => {
      const next = new Set(current);
      next.delete(vendorId);
      return next;
    });
    requireCarrierReview(target);
    if (target) removeEmptyDraft(target, undefined, vendorId);
    markDirty();
  };

  const setAccountServiceType = (
    accountId: number,
    target: CarrierTarget,
    serviceDefinitionId: string
  ) => {
    setAccountServiceDefinitionIds((current) => {
      const next = { ...current };
      if (serviceDefinitionId) next[accountId] = serviceDefinitionId;
      else delete next[accountId];
      return next;
    });
    requireCarrierReview(target);
    markDirty();
  };

  const setVendorServiceType = (
    vendorId: number,
    target: CarrierTarget,
    serviceDefinitionId: string
  ) => {
    setVendorServiceDefinitionIds((current) => {
      const next = { ...current };
      if (serviceDefinitionId) next[vendorId] = serviceDefinitionId;
      else delete next[vendorId];
      return next;
    });
    requireCarrierReview(target);
    markDirty();
  };

  const save = async () => {
    if (!mapping || disabled || saving) return false;
    const activeNames = localDrafts.map(
      (draft) => names[localDraftTarget(draft)] ?? ''
    );
    const validationError = validateCarrierNames(activeNames);
    if (validationError) {
      setError(validationError);
      return false;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const drafts = localDrafts.map((draft) => {
        const target = localDraftTarget(draft);
        return {
          id: draft.id,
          name: names[target]!.trim(),
          providerAccountIds: accounts
            .filter((account) => accountAssignments[account.providerAccountId] === target)
            .map((account) => account.providerAccountId),
          providerVendorIds: vendors
            .filter((vendor) => vendorAssignments[vendor.providerCarrierId] === target)
            .map((vendor) => vendor.providerCarrierId),
          accountServiceAssignments: accounts
            .filter((account) => accountAssignments[account.providerAccountId] === target)
            .map((account) => ({
              providerAccountId: account.providerAccountId,
              serviceDefinitionId: accountServiceDefinitionIds[account.providerAccountId] ?? null,
            })),
          vendorServiceAssignments: vendors
            .filter((vendor) => vendorAssignments[vendor.providerCarrierId] === target)
            .map((vendor) => ({
              providerCarrierId: vendor.providerCarrierId,
              serviceDefinitionId: vendorServiceDefinitionIds[vendor.providerCarrierId] ?? null,
            })),
          reviewStatus: reviewStatuses[target] ?? 'not_required',
          reviewNow: reviewNowTargets.has(target),
        };
      });
      applyMapping(await api.carrierInitialization.replaceMappingDrafts({
        jobId: mapping.jobId,
        expectedJobRevision: mapping.jobRevision,
        drafts,
      }));
      setFolderPublication(await api.carrierInitialization.getFolderPublication());
      setSaved(true);
      return true;
    } catch (saveError) {
      setError(errorMessage(saveError));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const publishCarrierFolders = async () => {
    if (disabled || saving || publishingFolders || dirty) return;
    setPublishingFolders(true);
    setError(null);
    folderPublicationActive.current = true;
    try {
      await runCarrierFolderPublication({
        advance: () => api.carrierInitialization.advanceFolderPublication(),
        onProgress: (progress) => {
          setFolderPublication(progress);
          if (progress.trafficHistoryEffectiveDate.providerCalendarDate) {
            setTrafficHistoryEffectiveDate(
              progress.trafficHistoryEffectiveDate.providerCalendarDate
            );
          }
        },
        shouldContinue: () => folderPublicationActive.current,
      });
    } catch (publicationError) {
      setError(errorMessage(publicationError));
    } finally {
      folderPublicationActive.current = false;
      setPublishingFolders(false);
    }
  };

  const saveEffectiveDate = async (date = trafficHistoryEffectiveDate) => {
    if (!date || savingEffectiveDate || disabled || dirty) return;
    setSavingEffectiveDate(true);
    setError(null);
    try {
      const progress = await api.carrierInitialization.saveTrafficHistoryEffectiveDate(date);
      setFolderPublication(progress);
      setTrafficHistoryEffectiveDate(
        progress.trafficHistoryEffectiveDate.providerCalendarDate ?? ''
      );
      setSaved(true);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setSavingEffectiveDate(false);
    }
  };

  const changeDisposition = async (input: {
    recordType: CarrierRecordType;
    providerRecordId: number;
    action: 'ignore' | 'restore';
    reason?: CarrierRecordIgnoreReason;
  }) => {
    if (!mapping || disabled || saving) return;
    if (dirty) {
      setError('Save your Carrier changes before changing ignored records.');
      return;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const result = await api.carrierInitialization.changeRecordDisposition({
        jobId: mapping.jobId,
        expectedJobRevision: mapping.jobRevision,
        ...input,
      });
      applyMapping(result);
      setFolderPublication(await api.carrierInitialization.getFolderPublication());
      setQuery('');
      if (input.action === 'ignore') {
        setView('ignored');
        setSelectedIgnoredKey(`${input.recordType}:${input.providerRecordId}`);
      } else if (input.recordType === 'vendor_carrier') {
        setView('vendors');
        setSelectedVendorId(input.providerRecordId);
      } else {
        setView('accounts');
        setSelectedAccountId(
          input.recordType === 'customer_account' ? input.providerRecordId : null
        );
      }
      setIgnoreTarget(null);
      setIgnoreReason('');
      setSaved(true);
    } catch (dispositionError) {
      setError(errorMessage(dispositionError));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="mt-3 flex items-center gap-2 text-xs text-surface-500 dark:text-surface-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Preparing Carriers…
      </p>
    );
  }
  if (!mapping) {
    return <CarrierOrganizerMissingMapping error={error} />;
  }
  if (!mapping.ready) {
    return (
      <p className="mt-3 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {blockedMessage(mapping.blockedReason)}
      </p>
    );
  }
  if (mapping.groups.length === 0 && vendors.length === 0 && mapping.ignoredRecords.length === 0) {
    return <p className="mt-3 text-xs text-surface-500 dark:text-surface-400">No Carrier records were found.</p>;
  }

  // Release 1 proposals (ADR-0024): shown beside each trunk, applied only by
  // Accept proposals for one Carrier or by a single vendor's Use proposal.
  const vendorTargets = proposedVendorTargets({
    accounts,
    vendors,
    accountAssignments,
    vendorAssignments,
    carrierTargets: new Set(localDrafts.map(localDraftTarget)),
  });
  const newCarrierNames: Record<string, string> = {};
  for (const vendor of vendors) {
    const target = vendorTargets[vendor.providerCarrierId];
    if (target?.startsWith(PROPOSED_NEW_CARRIER_PREFIX) && vendor.proposedNewCarrierName) {
      newCarrierNames[target] = vendor.proposedNewCarrierName;
    }
  }
  const proposalPlanFor = (target: CarrierTarget) => carrierProposalPlan({
    target,
    accounts,
    vendors,
    accountAssignments,
    vendorAssignments,
    accountServiceDefinitionIds,
    vendorServiceDefinitionIds,
    vendorTargets,
  });
  const acceptCarrierProposals = (target: CarrierTarget) => {
    const plan = proposalPlanFor(target);
    if (plan.count === 0) return;
    if (plan.vendorIds.length > 0) {
      setVendorAssignments((current) => {
        const next = { ...current };
        for (const vendorId of plan.vendorIds) {
          if (!next[vendorId]) next[vendorId] = target;
        }
        return next;
      });
    }
    // A value already set in the edited mapping always wins over a proposal.
    setAccountServiceDefinitionIds((current) => ({ ...plan.accountServices, ...current }));
    setVendorServiceDefinitionIds((current) => ({ ...plan.vendorServices, ...current }));
    requireCarrierReview(target);
    markDirty();
  };
  // Use proposal / Add for one vendor: its Carrier, and its proposed Service
  // Type when the vendor has none yet.
  const acceptVendorProposal = (vendor: (typeof vendors)[number], target: string) => {
    assignVendor(vendor, target);
    const serviceDefinitionId = vendor.proposedService?.serviceDefinitionId;
    if (serviceDefinitionId) {
      setVendorServiceDefinitionIds((current) => (
        current[vendor.providerCarrierId]
          ? current
          : { ...current, [vendor.providerCarrierId]: serviceDefinitionId }
      ));
    }
  };
  const proposalLine = (proposal: { serviceDefinitionId: string | null; reason: string } | undefined) => {
    if (!proposal) return null;
    const definition = mapping.serviceDefinitions.find(
      (candidate) => candidate.id === proposal.serviceDefinitionId
    );
    return (
      <span className="block text-[0.68rem] text-sky-700 dark:text-sky-300">
        {definition ? `Proposed: ${definition.name} · ` : 'Hint: '}{proposal.reason}
      </span>
    );
  };

  const carriers = localDrafts.map((draft) => {
    const target = localDraftTarget(draft);
    const proposalCount = proposalPlanFor(target).count;
    const accountCount = accounts.filter(
      (account) => accountAssignments[account.providerAccountId] === target
    ).length;
    const vendorCountForCarrier = vendors.filter(
      (vendor) => vendorAssignments[vendor.providerCarrierId] === target
    ).length;
    const missingServiceCount = accounts.filter(
      (account) => accountAssignments[account.providerAccountId] === target
        && !accountServiceDefinitionIds[account.providerAccountId]
    ).length + vendors.filter(
      (vendor) => vendorAssignments[vendor.providerCarrierId] === target
        && !vendorServiceDefinitionIds[vendor.providerCarrierId]
    ).length;
    return {
      target,
      trunkCount: accountCount + vendorCountForCarrier,
      name: names[target] ?? 'Unnamed Carrier',
      meta: `${accountCount} Inbound · ${vendorCountForCarrier} Outbound${missingServiceCount > 0 ? ` · ${missingServiceCount} need Service Type` : ''}${proposalCount > 0 ? ` · ${proposalCount} proposed` : ''}`,
      attention: reviewStatuses[target] === 'needs_review' || missingServiceCount > 0,
      missingServiceCount,
    };
  });
  const needsReviewCount = carriers.filter((carrier) => carrier.attention).length;
  const confirmCarrierMapping = (target: CarrierTarget) => {
    if ((carriers.find((carrier) => carrier.target === target)?.missingServiceCount ?? 0) > 0) {
      setError('Assign one Service Type to every Inbound and Outbound trunk before confirmation.');
      return;
    }
    setReviewStatuses((current) => ({
      ...current,
      [target]: 'confirmed',
    }));
    setReviewNowTargets((current) => new Set(current).add(target));
    const nextCarrier = carriers.find((carrier) => (
      carrier.target !== target && reviewStatuses[carrier.target] === 'needs_review'
    ));
    setSelectedCarrier(nextCarrier?.target ?? target);
    markDirty();
  };
  const tableRows = buildCarrierMappingTableRows({
    accounts,
    vendors,
    accountAssignments,
    vendorAssignments,
    accountServiceDefinitionIds,
    vendorServiceDefinitionIds,
    vendorTargets,
  });
  const confirmableCarrierTargets = carriers
    .filter((carrier) => (
      reviewStatuses[carrier.target] === 'needs_review'
      && carrier.missingServiceCount === 0
      && carrier.trunkCount > 0
    ))
    .map((carrier) => carrier.target);
  const findAccount = (id: number) => accounts.find((account) => account.providerAccountId === id);
  const findVendor = (id: number) => vendors.find((vendor) => vendor.providerCarrierId === id);
  // Creates the local Carrier drafts for accepted proposed new Carriers and
  // returns the real target for each proposed target.
  const materializeTargets = (targets: string[]): Record<string, CarrierTarget> => {
    const resolved: Record<string, CarrierTarget> = {};
    const created: Array<{ key: string; target: CarrierTarget; name: string }> = [];
    for (const target of new Set(targets)) {
      if (!target.startsWith(PROPOSED_NEW_CARRIER_PREFIX)) {
        resolved[target] = target as CarrierTarget;
        continue;
      }
      // Reuse an existing Carrier with the same name instead of a duplicate.
      const proposedName = newCarrierNames[target] ?? '';
      const sameName = localDrafts.map(localDraftTarget).find((existing) => (
        (names[existing] ?? '').toLocaleLowerCase('en-US').replace(/[^\p{L}\p{N}]+/gu, '')
          === proposedName.toLocaleLowerCase('en-US').replace(/[^\p{L}\p{N}]+/gu, '')
      ));
      if (proposedName && sameName) {
        resolved[target] = sameName;
        continue;
      }
      const key = `proposed-${encodeURIComponent(target.slice(PROPOSED_NEW_CARRIER_PREFIX.length))}`;
      const realTarget = carrierTarget(key);
      resolved[target] = realTarget;
      created.push({ key, target: realTarget, name: newCarrierNames[target] ?? key });
    }
    if (created.length > 0) {
      setLocalDrafts((current) => [
        ...current,
        ...created.filter((entry) => !current.some((draft) => draft.key === entry.key)).map((entry) => ({ key: entry.key })),
      ]);
      setNames((current) => {
        const next = { ...current };
        for (const entry of created) next[entry.target] = next[entry.target] ?? entry.name;
        return next;
      });
    }
    return resolved;
  };
  const setTableCarrier = (row: CarrierMappingTableRow, target: string) => {
    if (row.direction === 'inbound') {
      const account = findAccount(row.providerRecordId);
      if (!account) return;
      if (target) assignAccount(account, target);
      else removeAccount(row.providerRecordId);
      return;
    }
    const vendor = findVendor(row.providerRecordId);
    if (!vendor) return;
    if (!target) {
      removeVendor(row.providerRecordId);
      return;
    }
    const realTarget = target === 'new' ? 'new' : materializeTargets([target])[target]!;
    assignVendor(vendor, realTarget);
  };
  const setTableService = (row: CarrierMappingTableRow, serviceDefinitionId: string) => {
    if (!row.carrierTarget) return;
    // Clearing a value that is only proposed changes nothing and accepts nothing.
    if (!serviceDefinitionId && (row.serviceProposed || row.carrierProposed)) return;
    if (row.direction === 'inbound') {
      setAccountServiceType(row.providerRecordId, row.carrierTarget as CarrierTarget, serviceDefinitionId);
      return;
    }
    // Choosing a Service Type for a vendor with a proposed Carrier accepts the Carrier too.
    const target = materializeTargets([row.carrierTarget])[row.carrierTarget]!;
    const vendor = findVendor(row.providerRecordId);
    if (row.carrierProposed && vendor) assignVendor(vendor, target);
    setVendorServiceType(row.providerRecordId, target, serviceDefinitionId);
  };
  const acceptTableRows = (rows: CarrierMappingTableRow[]) => {
    const resolved = materializeTargets(rows.flatMap((row) => (row.carrierTarget ? [row.carrierTarget] : [])));
    const vendorTargetsToAssign: Record<number, CarrierTarget> = {};
    const accountServices: Record<number, string> = {};
    const vendorServices: Record<number, string> = {};
    const touched = new Set<CarrierTarget>();
    for (const row of rows) {
      const target = row.carrierTarget ? resolved[row.carrierTarget] : undefined;
      if (!target) continue;
      if (row.direction === 'outbound' && row.carrierProposed) {
        vendorTargetsToAssign[row.providerRecordId] = target;
        touched.add(target);
      }
      if (row.serviceProposed && row.serviceDefinitionId) {
        if (row.direction === 'inbound') accountServices[row.providerRecordId] = row.serviceDefinitionId;
        else vendorServices[row.providerRecordId] = row.serviceDefinitionId;
        touched.add(target);
      }
    }
    if (touched.size === 0) return;
    setVendorAssignments((current) => ({ ...vendorTargetsToAssign, ...current }));
    setAccountServiceDefinitionIds((current) => ({ ...accountServices, ...current }));
    setVendorServiceDefinitionIds((current) => ({ ...vendorServices, ...current }));
    requireCarrierReview(...touched);
    markDirty();
  };
  const renameCarrier = (target: string, name: string) => {
    setNames((current) => ({ ...current, [target]: name }));
    requireCarrierReview(target as CarrierTarget);
    markDirty();
  };
  // Ignores the given trunks with one reason. Pending edits are saved first,
  // then each trunk is ignored against the latest revision; an ignored trunk
  // leaves its Carrier, which goes back to review.
  const ignoreTableRows = async (
    rows: CarrierMappingTableRow[],
    reason: CarrierRecordIgnoreReason
  ): Promise<number[]> => {
    if (!mapping || disabled || saving || rows.length === 0) return [];
    if (dirty && !(await save())) return [];
    const ignored: number[] = [];
    setSaving(true);
    setSaved(false);
    setError(null);
    const failures: string[] = [];
    try {
      let current = await api.carrierInitialization.getMappingDrafts();
      for (const row of rows) {
        if (!current) break;
        try {
          current = await api.carrierInitialization.changeRecordDisposition({
            jobId: current.jobId,
            expectedJobRevision: current.jobRevision,
            recordType: row.direction === 'inbound' ? 'customer_account' : 'vendor_carrier',
            providerRecordId: row.providerRecordId,
            action: 'ignore',
            reason,
          });
          ignored.push(rows.indexOf(row));
        } catch (ignoreError) {
          failures.push(`${row.name}: ${errorMessage(ignoreError)}`);
        }
      }
      if (current) applyMapping(current);
      setFolderPublication(await api.carrierInitialization.getFolderPublication());
      if (failures.length > 0) {
        setError(`${failures.length} of ${rows.length} not ignored. ${failures.slice(0, 3).join(' · ')}`);
      } else {
        setSaved(true);
      }
    } catch (ignoreError) {
      setError(errorMessage(ignoreError));
    } finally {
      setSaving(false);
    }
    return ignored;
  };
  const restoreIgnoredRecord = (record: CarrierInitializationIgnoredRecord) => {
    void changeDisposition({
      recordType: record.recordType,
      providerRecordId: record.providerRecordId,
      action: 'restore',
    });
  };
  const confirmCompleteCarriers = () => {
    if (confirmableCarrierTargets.length === 0) return;
    setReviewStatuses((current) => {
      const next = { ...current };
      for (const target of confirmableCarrierTargets) next[target] = 'confirmed';
      return next;
    });
    setReviewNowTargets((current) => {
      const next = new Set(current);
      for (const target of confirmableCarrierTargets) next.add(target);
      return next;
    });
    markDirty();
  };
  const carrierOptions = carriers.map((carrier) => ({
    value: carrier.target,
    label: carrier.name,
  }));
  const normalizedQuery = query.trim().toLocaleLowerCase('en-US');
  const filteredCarriers = carriers.filter((carrier) => (
    (!normalizedQuery || carrier.name.toLocaleLowerCase('en-US').includes(normalizedQuery))
    && (view !== 'attention' || carrier.attention)
  ));
  const filteredUnassignedAccounts = unassignedAccounts.filter((account) => (
    !normalizedQuery
    || account.name.toLocaleLowerCase('en-US').includes(normalizedQuery)
    || account.company.name.toLocaleLowerCase('en-US').includes(normalizedQuery)
  ));
  const filteredUnassignedVendors = unassignedVendors.filter((vendor) => (
    !normalizedQuery || vendor.name.toLocaleLowerCase('en-US').includes(normalizedQuery)
  ));
  const filteredIgnored = mapping.ignoredRecords.filter((record) => (
    !normalizedQuery || record.name.toLocaleLowerCase('en-US').includes(normalizedQuery)
  ));
  const selectedLocalDraft = localDrafts.find(
    (draft) => localDraftTarget(draft) === selectedCarrier
  );
  const selectedReviewStatus = selectedCarrier
    ? reviewStatuses[selectedCarrier] ?? 'not_required'
    : 'not_required';
  const selectedSourceGroup = selectedLocalDraft?.sourceCompanyId === undefined
    ? undefined
    : mapping.groups.find(
        (group) => group.providerCompanyId === selectedLocalDraft.sourceCompanyId
      );
  const selectedCarrierName = selectedCarrier ? names[selectedCarrier] ?? '' : '';
  const selectedAssignedAccounts = selectedCarrier
    ? accounts.filter(
        (account) => accountAssignments[account.providerAccountId] === selectedCarrier
      )
    : [];
  const selectedAssignedVendors = selectedCarrier
    ? vendors.filter((vendor) => vendorAssignments[vendor.providerCarrierId] === selectedCarrier)
    : [];
  const selectedMissingServiceCount = selectedAssignedAccounts.filter(
    (account) => !accountServiceDefinitionIds[account.providerAccountId]
  ).length + selectedAssignedVendors.filter(
    (vendor) => !vendorServiceDefinitionIds[vendor.providerCarrierId]
  ).length;
  const selectedProposalCount = selectedCarrier ? proposalPlanFor(selectedCarrier).count : 0;
  const selectedProposedVendors = selectedCarrier
    ? unassignedVendors.filter((vendor) => vendorTargets[vendor.providerCarrierId] === selectedCarrier)
    : [];
  const selectedSuggestions = selectedSourceGroup
    ? unassignedVendors.filter(
        (vendor) => vendor.suggestedProviderCompanyId === selectedSourceGroup.providerCompanyId
      )
    : [];
  const selectedUnassignedAccount = unassignedAccounts.find(
    (account) => account.providerAccountId === selectedAccountId
  );
  const selectedUnassignedVendor = unassignedVendors.find(
    (vendor) => vendor.providerCarrierId === selectedVendorId
  );
  const selectedIgnored = mapping.ignoredRecords.find(
    (record) => ignoredKey(record) === selectedIgnoredKey
  );
  const pickerVendors = unassignedVendors.filter((vendor) => (
    !pickerQuery.trim()
    || vendor.name.toLocaleLowerCase('en-US').includes(
      pickerQuery.trim().toLocaleLowerCase('en-US')
    )
  ));
  const pickerAccounts = unassignedAccounts.filter((account) => (
    !accountPickerQuery.trim()
    || account.name.toLocaleLowerCase('en-US').includes(
      accountPickerQuery.trim().toLocaleLowerCase('en-US')
    )
    || account.company.name.toLocaleLowerCase('en-US').includes(
      accountPickerQuery.trim().toLocaleLowerCase('en-US')
    )
  ));
  const todayDate = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  })();
  const effectiveDateState = folderPublication
    ? carrierTrafficHistoryEffectiveDateState(folderPublication)
    : null;

  const auditAction = carrierAuditActionState({
    mapping,
    folderPublication,
    audit,
    dirty,
    activeSessionId,
  });

  // Starts or resumes the read-only Carrier audit for this chat.
  const checkSwitchPlatform = async () => {
    if (!activeSessionId || !auditAction.enabled || auditStarting) return;
    setAuditStarting(true);
    setError(null);
    try {
      await api.carrierInitialization.startCarrierAudit(activeSessionId);
      await refreshSession(activeSessionId);
      setAudit(await api.carrierInitialization.getCarrierAudit());
    } catch (auditError) {
      if (mountedRef.current) setError(errorMessage(auditError));
    } finally {
      if (mountedRef.current) setAuditStarting(false);
    }
  };

  const organizerNotices = (
    <>
      {folderPublication && !dirty && carrierFolderPublicationBlockedMessage(folderPublication) && (
              <div role="status" className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{carrierFolderPublicationBlockedMessage(folderPublication)}</span>
              </div>
            )}
      {effectiveDateState?.visible && (
              <div className="flex flex-wrap items-end justify-between gap-3 border-b border-surface-200 bg-surface-50 px-4 py-3 dark:border-surface-800 dark:bg-surface-950">
                <div>
                  <p className="text-xs font-semibold text-surface-900 dark:text-surface-100">
                    Traffic History mapping
                  </p>
                  <p className={`mt-1 text-xs ${effectiveDateState.needsSelection ? 'text-amber-700 dark:text-amber-300' : 'text-surface-500 dark:text-surface-400'}`}>
                    {effectiveDateState.message}
                  </p>
                </div>
                <div className="flex items-end gap-2">
                  {effectiveDateState.needsSelection && !trafficHistoryEffectiveDate && (
                    <button type="button" onClick={() => setTrafficHistoryEffectiveDate(todayDate)} title="Fills in today's date in this browser. Check it before you save." className="h-8 rounded-lg border border-surface-300 px-2.5 text-xs font-semibold dark:border-surface-700">
                      Use today
                    </button>
                  )}
                  <label className="text-xs font-medium text-surface-600 dark:text-surface-300">
                    <span className="mb-1 block">Mapping effective date</span>
                    <Input
                      aria-label="Mapping effective date"
                      type="date"
                      value={trafficHistoryEffectiveDate}
                      onChange={(event) => setTrafficHistoryEffectiveDate(event.target.value)}
                      className="h-8 w-40 text-xs"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={disabled || dirty || saving || publishingFolders || savingEffectiveDate || !trafficHistoryEffectiveDate || !folderPublication?.ready}
                    onClick={() => void saveEffectiveDate()}
                    className="flex h-8 items-center gap-1.5 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-surface-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {savingEffectiveDate && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Save effective date
                  </button>
                </div>
              </div>
            )}
    </>
  );
  const publishButton = folderPublication && (
                  <button
                    type="button"
                    disabled={disabled || saving || publishingFolders || dirty || !folderPublication.ready || folderPublication.pendingCarriers === 0}
                    onClick={() => void publishCarrierFolders()}
                    title={!folderPublication.ready
                      ? carrierFolderPublicationBlockedMessage(folderPublication) ?? undefined
                      : dirty
                        ? 'Save Carrier changes before publishing folders.'
                        : undefined}
                    className="flex h-8 items-center gap-1.5 rounded-lg border border-surface-300 px-3 text-xs font-semibold text-surface-700 hover:border-emerald-500 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-surface-700 dark:text-surface-200 dark:hover:text-emerald-300"
                  >
                    {publishingFolders
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : folderPublication.pendingCarriers === 0
                        ? <CheckCircle2 className="h-3.5 w-3.5" />
                        : <RotateCcw className="h-3.5 w-3.5" />}
                    {folderPublication.pendingCarriers === 0
                      ? 'Folders current'
                      : folderPublication.currentCarriers > 0
                        ? `Resume folders (${folderPublication.pendingCarriers})`
                        : `Publish folders (${folderPublication.pendingCarriers})`}
                  </button>
  );

  const auditButton = auditAction.visible && (
    <button
      type="button"
      disabled={disabled || saving || auditStarting || !auditAction.enabled || !activeSessionId}
      onClick={() => void checkSwitchPlatform()}
      title={auditAction.reason ?? undefined}
      className="flex h-8 items-center gap-1.5 rounded-lg border border-surface-300 px-3 text-xs font-semibold text-surface-700 hover:border-amber-500 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-surface-700 dark:text-surface-200 dark:hover:text-amber-300"
    >
      {auditStarting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {auditAction.label}
    </button>
  );

  const openIgnore = (target: IgnoreTarget) => {
    if (dirty) {
      setError('Save your Carrier changes before changing ignored records.');
      return;
    }
    setIgnoreTarget(target);
    setIgnoreReason('');
  };

  return (
    <>
      {!organizerOpen && (
        <section className="mt-4 rounded-lg border border-surface-200 bg-white p-3 dark:border-surface-700 dark:bg-surface-900">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-surface-900 dark:text-surface-100">Carrier setup is ready</p>
              <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                {carriers.length} Carriers · {needsReviewCount} to review · {unassignedAccounts.length + unassignedVendors.length} unassigned · {mapping.ignoredRecords.length} ignored
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {auditButton}
              {publishButton}
              <button type="button" disabled={disabled} onClick={() => setTableOpen(true)} className="h-8 rounded-lg bg-surface-900 px-3 text-xs font-semibold text-white hover:bg-surface-700 disabled:opacity-50 dark:bg-white dark:text-surface-950 dark:hover:bg-surface-200">
                Review all trunks
              </button>
            </div>
          </div>
        </section>
      )}

      {tableOpen && (
        <CarrierMappingTable
          rows={tableRows}
          carrierOptions={carrierOptions}
          serviceDefinitions={mapping.serviceDefinitions}
          confirmableCarrierCount={confirmableCarrierTargets.length}
          summary={`${tableRows.length} trunks · ${carriers.length} Carriers · ${needsReviewCount} Carriers to review${dirty ? ' · unsaved changes' : ''}`}
          disabled={disabled}
          saving={saving}
          dirty={dirty}
          error={error}
          onClose={() => {
            setTableOpen(false);
            setOrganizerOpen(false);
          }}
          headerActions={publishButton}
          newCarrierNames={newCarrierNames}
          carrierNames={names}
          ignoredRecords={mapping.ignoredRecords}
          ignoreReasons={IGNORE_REASONS}
          onRenameCarrier={renameCarrier}
          onIgnore={ignoreTableRows}
          notices={organizerNotices}
          onRestore={restoreIgnoredRecord}
          onSave={() => void save()}
          onSetCarrier={setTableCarrier}
          onSetService={setTableService}
          onAccept={acceptTableRows}
          onConfirmComplete={confirmCompleteCarriers}
        />
      )}

      {organizerOpen && (
        <div className="mt-4 w-full">
          <section aria-label="Carrier Organizer" className="flex h-[min(48rem,calc(100vh-12rem))] w-full flex-col overflow-hidden rounded-xl border border-surface-300 bg-surface-50 shadow-xl dark:border-surface-700 dark:bg-surface-950">
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-200 bg-white px-4 py-3 dark:border-surface-800 dark:bg-surface-900">
              <div>
                <p className="text-[0.65rem] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">Carrier setup</p>
                <h3 className="text-lg font-semibold text-surface-950 dark:text-white">Organize Carriers</h3>
                <p className="text-xs text-surface-500 dark:text-surface-400">{carriers.length} Carriers · {needsReviewCount} to review · {unassignedAccounts.length + unassignedVendors.length} unassigned · {mapping.ignoredRecords.length} ignored</p>
              </div>
              <div className="flex items-center gap-2">
                {saved && <span className="hidden items-center gap-1 text-xs text-emerald-700 sm:flex dark:text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" />Saved</span>}
                {auditButton}
                {publishButton}
                <button type="button" disabled={disabled} onClick={() => setTableOpen(true)} className="h-8 rounded-lg border border-sky-500 bg-sky-50 px-3 text-xs font-semibold text-sky-900 hover:bg-sky-100 disabled:opacity-50 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-100">
                  Review all trunks
                </button>
                <button type="button" disabled={saving} onClick={() => {
                  folderPublicationActive.current = false;
                  setOrganizerOpen(false);
                }} className="h-8 px-2 text-xs font-semibold text-surface-500 hover:text-surface-900 disabled:opacity-50 dark:hover:text-white">Close</button>
                <button type="button" disabled={disabled || saving || publishingFolders || !dirty} onClick={() => void save()} className="flex h-8 items-center gap-1.5 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-surface-950 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Save changes
                </button>
              </div>
            </header>

            {organizerNotices}

            {error && (
              <div role="alert" className="flex items-start gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[19rem_minmax(0,1fr)]">
              <aside className="flex min-h-0 flex-col border-b border-surface-200 bg-surface-100 p-3 md:border-b-0 md:border-r dark:border-surface-800 dark:bg-surface-900">
                <label className="relative block">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-surface-400" />
                  <Input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={view === 'accounts'
                      ? 'Search Inbound trunks…'
                      : view === 'vendors'
                        ? 'Search Outbound trunks…'
                        : view === 'ignored'
                          ? 'Search ignored records…'
                          : 'Search Carriers…'}
                    className="h-8 pl-8 text-xs"
                  />
                </label>
                <div className="mt-2 grid grid-cols-5 gap-1 rounded-lg bg-surface-200 p-1 dark:bg-surface-800">
                  {([
                    ['attention', 'Attention'],
                    ['all', 'Carriers'],
                    ['accounts', 'Inbound'],
                    ['vendors', 'Outbound'],
                    ['ignored', 'Ignored'],
                  ] as const).map(([value, label]) => (
                    <button key={value} type="button" onClick={() => {
                      setView(value);
                      setQuery('');
                    }} className={`min-w-0 rounded-md px-1 py-1.5 text-[0.62rem] font-semibold ${view === value ? 'bg-white text-surface-950 shadow-sm dark:bg-surface-700 dark:text-white' : 'text-surface-500 hover:text-surface-900 dark:hover:text-white'}`}>{label}</button>
                  ))}
                </div>
                <p className="px-2 pb-1 pt-3 text-[0.62rem] font-bold uppercase tracking-wider text-surface-400">
                  {view === 'attention'
                    ? 'Suggestions to review'
                    : view === 'all'
                      ? 'Organized Carriers'
                      : view === 'accounts'
                        ? 'Inbound trunks without a Carrier'
                        : view === 'vendors'
                          ? 'Outbound trunks without a Carrier'
                          : 'Retained disposition history'}
                </p>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {(view === 'attention' || view === 'all') && filteredCarriers.map((carrier) => (
                    <button key={carrier.target} type="button" onClick={() => setSelectedCarrier(carrier.target)} className={`my-0.5 w-full rounded-lg border px-2.5 py-2 text-left ${selectedCarrier === carrier.target ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30' : 'border-transparent hover:bg-white dark:hover:bg-surface-800'}`}>
                      <span className="block truncate text-xs font-semibold text-surface-900 dark:text-surface-100">{carrier.attention && <span className="mr-1.5 text-amber-500">●</span>}{carrier.name}</span>
                      <span className="mt-0.5 block text-[0.68rem] text-surface-500 dark:text-surface-400">{carrier.meta}</span>
                    </button>
                  ))}
                  {view === 'accounts' && filteredUnassignedAccounts.map((account) => (
                    <button key={account.providerAccountId} type="button" onClick={() => setSelectedAccountId(account.providerAccountId)} className={`my-0.5 w-full rounded-lg border px-2.5 py-2 text-left ${selectedAccountId === account.providerAccountId ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30' : 'border-transparent hover:bg-white dark:hover:bg-surface-800'}`}>
                      <span title={account.name} className="block break-words text-xs font-semibold text-surface-900 dark:text-surface-100">{account.name}</span>
                      <span title={account.company.name} className="mt-0.5 block break-words text-[0.68rem] text-surface-500 dark:text-surface-400">{account.company.name}</span>
                    </button>
                  ))}
                  {view === 'vendors' && filteredUnassignedVendors.map((vendor) => (
                    <button key={vendor.providerCarrierId} type="button" onClick={() => setSelectedVendorId(vendor.providerCarrierId)} className={`my-0.5 w-full rounded-lg border px-2.5 py-2 text-left ${selectedVendorId === vendor.providerCarrierId ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30' : 'border-transparent hover:bg-white dark:hover:bg-surface-800'}`}>
                      <span title={vendor.name} className="block break-words text-xs font-semibold text-surface-900 dark:text-surface-100">{vendor.name}</span>
                      <span className="mt-0.5 block text-[0.68rem] text-surface-500 dark:text-surface-400">Outbound trunk</span>
                    </button>
                  ))}
                  {view === 'ignored' && filteredIgnored.map((record) => (
                    <button key={ignoredKey(record)} type="button" onClick={() => setSelectedIgnoredKey(ignoredKey(record))} className={`my-0.5 w-full rounded-lg border px-2.5 py-2 text-left ${selectedIgnoredKey === ignoredKey(record) ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/30' : 'border-transparent hover:bg-white dark:hover:bg-surface-800'}`}>
                      <span title={record.name} className="block break-words text-xs font-semibold text-surface-900 dark:text-surface-100">{record.reviewRequired && <span className="mr-1.5 text-amber-500">●</span>}{record.name}</span>
                      <span className="mt-0.5 block text-[0.68rem] text-surface-500 dark:text-surface-400">{record.reviewRequired ? 'Review again' : reasonLabel(record.reason)}</span>
                    </button>
                  ))}
                </div>
              </aside>

              <main className="min-h-0 overflow-y-auto bg-white p-4 dark:bg-surface-950 sm:p-5">
                {(view === 'attention' || view === 'all') && selectedCarrier && selectedLocalDraft && (
                  <div className="mx-auto max-w-3xl">
                    <div className="flex items-start justify-between gap-3 border-b border-surface-200 pb-4 dark:border-surface-800">
                      <div>
                        <h4 className="text-base font-semibold text-surface-950 dark:text-white">{selectedCarrierName}</h4>
                        <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">
                          {selectedAssignedAccounts.length} Inbound · {selectedAssignedVendors.length} Outbound
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {selectedReviewStatus === 'confirmed' && (
                          <span className="flex h-8 items-center gap-1.5 px-2 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                            <CheckCircle2 className="h-3.5 w-3.5" />Confirmed
                          </span>
                        )}
                        {selectedMissingServiceCount > 0 && (
                          <span className="flex h-8 items-center px-2 text-xs font-semibold text-amber-700 dark:text-amber-300">
                            {selectedMissingServiceCount} Service {selectedMissingServiceCount === 1 ? 'Type' : 'Types'} needed
                          </span>
                        )}
                        {selectedProposalCount > 0 && (
                          <button type="button" disabled={disabled || saving} onClick={() => acceptCarrierProposals(selectedCarrier)} title="Apply the proposed Outbound trunks and Service Types of this Carrier. Review them, then save." className="flex h-8 items-center gap-1.5 rounded-lg border border-sky-400 px-3 text-xs font-semibold text-sky-800 hover:bg-sky-50 dark:border-sky-700 dark:text-sky-200 dark:hover:bg-sky-950/30">
                            Accept proposals ({selectedProposalCount})
                          </button>
                        )}
                        {selectedReviewStatus === 'needs_review' && (
                          <button type="button" disabled={disabled || saving || selectedMissingServiceCount > 0} onClick={() => confirmCarrierMapping(selectedCarrier)} title={selectedMissingServiceCount > 0 ? 'Assign a Service Type to every trunk first.' : undefined} className="flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50">
                            <CheckCircle2 className="h-3.5 w-3.5" />Confirm mapping
                          </button>
                        )}
                        {selectedSourceGroup && (
                        <button type="button" disabled={disabled || saving || dirty} onClick={() => openIgnore({
                          recordType: 'customer_company',
                          providerRecordId: selectedSourceGroup.providerCompanyId,
                          name: selectedSourceGroup.providerCompanyName,
                        })} className="flex h-8 items-center gap-1.5 rounded-lg border border-surface-300 px-2.5 text-xs font-semibold text-surface-600 hover:border-amber-500 hover:text-amber-700 disabled:opacity-40 dark:border-surface-700 dark:text-surface-300 dark:hover:text-amber-300">
                          <Archive className="h-3.5 w-3.5" />Ignore suggestion
                        </button>
                        )}
                      </div>
                    </div>
                    <label className="mt-4 block text-xs font-semibold text-surface-600 dark:text-surface-300">
                      Carrier name
                      <Input value={selectedCarrierName} disabled={disabled || saving} maxLength={256} onChange={(event) => {
                        setNames((current) => ({ ...current, [selectedCarrier]: event.target.value }));
                        requireCarrierReview(selectedCarrier);
                        markDirty();
                      }} className="mt-1 h-9 text-sm" />
                    </label>
                    <p className="mt-1.5 font-mono text-[0.7rem] text-surface-500 dark:text-surface-400">Future folder: {carrierFolderPreview(selectedCarrierName)}</p>

                    <section className="mt-5 rounded-lg border border-surface-200 dark:border-surface-800">
                      <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <div>
                          <p className="text-xs font-semibold text-surface-900 dark:text-surface-100">Inbound trunks</p>
                          <p className="mt-0.5 text-[0.68rem] text-surface-500 dark:text-surface-400">Inbound trunks assigned to this Carrier. Each trunk needs one Service Type.</p>
                        </div>
                        <button type="button" disabled={disabled || saving} onClick={() => {
                          setAccountPickerSelection(new Set());
                          setAccountPickerQuery('');
                          setAccountPickerOpen(true);
                        }} className="flex h-8 items-center gap-1 rounded-lg border border-surface-300 px-2.5 text-xs font-semibold hover:border-amber-500 dark:border-surface-700"><Plus className="h-3.5 w-3.5" />Add inbound</button>
                      </div>
                      {selectedAssignedAccounts.length === 0 ? (
                        <p className="border-t border-surface-200 px-3 py-3 text-xs text-surface-500 dark:border-surface-800 dark:text-surface-400">No Inbound trunks assigned.</p>
                      ) : (
                        <ul className="border-t border-surface-200 px-3 py-1 dark:border-surface-800">
                          {selectedAssignedAccounts.map((account) => (
                            <li key={account.providerAccountId} className="flex items-center justify-between gap-3 border-b border-surface-100 py-2 text-xs last:border-0 dark:border-surface-900">
                              <span className="min-w-0 flex-1">
                                <span title={account.name} className="block break-words text-surface-900 dark:text-surface-100">{account.name}</span>
                                <span title={account.company.name} className="block break-words text-[0.68rem] text-surface-500 dark:text-surface-400">{account.company.name}</span>
                                {!accountServiceDefinitionIds[account.providerAccountId] && proposalLine(account.proposedService)}
                              </span>
                              <div className="flex shrink-0 items-center gap-2">
                                <select
                                  aria-label={`Service Type for Inbound trunk ${account.name}`}
                                  disabled={disabled || saving || mapping.serviceDefinitions.length === 0}
                                  value={accountServiceDefinitionIds[account.providerAccountId] ?? ''}
                                  onChange={(event) => setAccountServiceType(account.providerAccountId, selectedCarrier, event.target.value)}
                                  className="h-8 w-44 rounded-lg border border-surface-300 bg-white px-2 text-xs text-surface-900 dark:border-surface-700 dark:bg-surface-950 dark:text-surface-100"
                                >
                                  <option value="">{mapping.serviceDefinitions.length === 0 ? 'Define Service Types first' : 'Select Service Type'}</option>
                                  {mapping.serviceDefinitions.map((definition) => (
                                    <option key={definition.id} value={definition.id}>{definition.name}</option>
                                  ))}
                                </select>
                                <button type="button" disabled={disabled || saving} onClick={() => removeAccount(account.providerAccountId)} aria-label={`Remove ${account.name} from ${selectedCarrierName}`} className="shrink-0 text-surface-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>

                    <section className="mt-4 rounded-lg border border-surface-200 dark:border-surface-800">
                      <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <div>
                          <p className="text-xs font-semibold text-surface-900 dark:text-surface-100">Outbound trunks</p>
                          <p className="mt-0.5 text-[0.68rem] text-surface-500 dark:text-surface-400">Outbound trunks assigned to this Carrier. Each trunk needs one Service Type.</p>
                        </div>
                        <button type="button" disabled={disabled || saving} onClick={() => {
                          setPickerSelection(new Set());
                          setPickerQuery('');
                          setPickerOpen(true);
                        }} className="flex h-8 items-center gap-1 rounded-lg border border-surface-300 px-2.5 text-xs font-semibold hover:border-amber-500 dark:border-surface-700"><Plus className="h-3.5 w-3.5" />Add outbound</button>
                      </div>
                      {selectedAssignedVendors.length === 0 ? (
                        <p className="border-t border-surface-200 px-3 py-3 text-xs text-surface-500 dark:border-surface-800 dark:text-surface-400">No Outbound trunks assigned.</p>
                      ) : (
                        <ul className="border-t border-surface-200 px-3 py-1 dark:border-surface-800">
                          {selectedAssignedVendors.map((vendor) => (
                            <li key={vendor.providerCarrierId} className="flex items-center justify-between gap-3 border-b border-surface-100 py-2 text-xs last:border-0 dark:border-surface-900">
                              <span className="min-w-0 flex-1">
                                <span title={vendor.name} className="block break-words">{vendor.name}</span>
                                {(vendor.assignmentSource === 'name_inference'
                                  || inferredVendorIds.has(vendor.providerCarrierId)) && (
                                  <span className="block text-[0.68rem] text-amber-700 dark:text-amber-400">Matched by name</span>
                                )}
                                {!vendorServiceDefinitionIds[vendor.providerCarrierId] && proposalLine(vendor.proposedService)}
                              </span>
                              <div className="flex shrink-0 items-center gap-2">
                                <select
                                  aria-label={`Service Type for Outbound trunk ${vendor.name}`}
                                  disabled={disabled || saving || mapping.serviceDefinitions.length === 0}
                                  value={vendorServiceDefinitionIds[vendor.providerCarrierId] ?? ''}
                                  onChange={(event) => setVendorServiceType(vendor.providerCarrierId, selectedCarrier, event.target.value)}
                                  className="h-8 w-44 rounded-lg border border-surface-300 bg-white px-2 text-xs text-surface-900 dark:border-surface-700 dark:bg-surface-950 dark:text-surface-100"
                                >
                                  <option value="">{mapping.serviceDefinitions.length === 0 ? 'Define Service Types first' : 'Select Service Type'}</option>
                                  {mapping.serviceDefinitions.map((definition) => (
                                    <option key={definition.id} value={definition.id}>{definition.name}</option>
                                  ))}
                                </select>
                                <button type="button" disabled={disabled || saving} onClick={() => removeVendor(vendor.providerCarrierId)} aria-label={`Remove ${vendor.name} from ${selectedCarrierName}`} className="text-surface-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>

                    {selectedProposedVendors.length > 0 && (
                      <section className="mt-4 rounded-lg border border-sky-300 bg-sky-50 p-3 dark:border-sky-900 dark:bg-sky-950/20">
                        <p className="text-xs font-semibold text-surface-900 dark:text-surface-100">Proposed Outbound trunks</p>
                        <ul className="mt-2 space-y-2">
                          {selectedProposedVendors.map((vendor) => (
                            <li key={vendor.providerCarrierId} className="flex items-start justify-between gap-3 text-xs">
                              <span className="min-w-0 flex-1">
                                <span className="block break-words font-semibold text-surface-900 dark:text-surface-100">{vendor.name}</span>
                                {vendor.proposalReason && <span className="block text-[0.68rem] text-surface-600 dark:text-surface-300">{vendor.proposalReason}</span>}
                                {proposalLine(vendor.proposedService)}
                              </span>
                              <button type="button" disabled={disabled || saving} onClick={() => acceptVendorProposal(vendor, selectedCarrier)} className="h-7 shrink-0 rounded-lg border border-sky-400 px-2.5 text-xs font-semibold text-sky-800 dark:border-sky-700 dark:text-sky-200">Add</button>
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}

                    {selectedSuggestions.map((vendor) => (
                      <section key={vendor.providerCarrierId} className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/20">
                        <p className="text-xs font-semibold text-surface-900 dark:text-surface-100">Suggested match</p>
                        <p className="mt-1 text-xs text-surface-600 dark:text-surface-300">{vendor.name} has the same unique name root as this Carrier.</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button type="button" disabled={disabled || saving} onClick={() => assignVendor(vendor, selectedCarrier)} className="h-8 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-surface-950 hover:bg-amber-400">Confirm match</button>
                          <button type="button" onClick={() => {
                            setView('vendors');
                            setSelectedVendorId(vendor.providerCarrierId);
                          }} className="h-8 px-2 text-xs font-semibold text-surface-500 hover:text-surface-900 dark:hover:text-white">Choose another Carrier</button>
                        </div>
                      </section>
                    ))}
                  </div>
                )}

                {view === 'accounts' && selectedUnassignedAccount && (
                  <div className="mx-auto max-w-2xl">
                    <h4 className="text-base font-semibold text-surface-950 dark:text-white">{selectedUnassignedAccount.name}</h4>
                    <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">Inbound trunk · {selectedUnassignedAccount.company.name}</p>
                    {proposalLine(selectedUnassignedAccount.proposedService)}
                    <p className="mt-5 text-xs leading-5 text-surface-600 dark:text-surface-300">This Inbound trunk is not assigned to a Carrier. Assign it below or create a new Carrier for it.</p>
                    <div className="mt-5 text-xs font-semibold text-surface-600 dark:text-surface-300">
                      <p>Assign to an existing Carrier</p>
                      <CarrierTargetPicker
                        ariaLabel="Assign Inbound trunk to a Carrier"
                        disabled={disabled || saving}
                        options={carrierOptions}
                        onSelect={(target) => assignAccount(selectedUnassignedAccount, target)}
                      />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button type="button" disabled={disabled || saving} onClick={() => assignAccount(selectedUnassignedAccount, 'new')} className="flex h-8 items-center gap-1.5 rounded-lg border border-surface-300 px-3 text-xs font-semibold hover:border-amber-500 dark:border-surface-700"><Plus className="h-3.5 w-3.5" />Create new Carrier</button>
                      <button type="button" disabled={disabled || saving || dirty} onClick={() => openIgnore({
                        recordType: 'customer_account',
                        providerRecordId: selectedUnassignedAccount.providerAccountId,
                        name: selectedUnassignedAccount.name,
                      })} className="flex h-8 items-center gap-1.5 rounded-lg border border-surface-300 px-3 text-xs font-semibold text-surface-600 hover:border-amber-500 hover:text-amber-700 disabled:opacity-40 dark:border-surface-700 dark:text-surface-300"><Archive className="h-3.5 w-3.5" />Ignore record</button>
                    </div>
                  </div>
                )}

                {view === 'vendors' && selectedUnassignedVendor && (
                  <div className="mx-auto max-w-2xl">
                    <h4 className="text-base font-semibold text-surface-950 dark:text-white">{selectedUnassignedVendor.name}</h4>
                    <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">Outbound trunk</p>
                    {proposalLine(selectedUnassignedVendor.proposedService)}
                    {selectedUnassignedVendor.suggestedProviderCompanyId !== null && (() => {
                      const suggested = mapping.groups.find((group) => (
                        group.providerCompanyId === selectedUnassignedVendor.suggestedProviderCompanyId
                      ));
                      return suggested ? (
                        <section className="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/20">
                          <p className="text-xs font-semibold">Suggested Carrier</p>
                          <p className="mt-1 text-xs text-surface-600 dark:text-surface-300">{names[companyTarget(suggested.providerCompanyId)] || suggested.providerCompanyName}</p>
                          <button type="button" disabled={disabled || saving} onClick={() => assignVendor(selectedUnassignedVendor, companyTarget(suggested.providerCompanyId))} className="mt-2 h-8 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-surface-950">Confirm match</button>
                        </section>
                      ) : null;
                    })()}
                    {vendorTargets[selectedUnassignedVendor.providerCarrierId] && (
                      <section className="mt-5 rounded-lg border border-sky-300 bg-sky-50 p-3 dark:border-sky-900 dark:bg-sky-950/20">
                        <p className="text-xs font-semibold">Proposed Carrier</p>
                        <p className="mt-1 text-xs text-surface-700 dark:text-surface-200">{names[vendorTargets[selectedUnassignedVendor.providerCarrierId] as CarrierTarget] ?? ''}</p>
                        {selectedUnassignedVendor.proposalReason && <p className="mt-1 text-[0.68rem] text-surface-600 dark:text-surface-300">{selectedUnassignedVendor.proposalReason}</p>}
                        <button type="button" disabled={disabled || saving} onClick={() => acceptVendorProposal(selectedUnassignedVendor, vendorTargets[selectedUnassignedVendor.providerCarrierId]!)} className="mt-2 h-8 rounded-lg border border-sky-400 px-3 text-xs font-semibold text-sky-800 dark:border-sky-700 dark:text-sky-200">Use proposal</button>
                      </section>
                    )}
                    <div className="mt-5 text-xs font-semibold text-surface-600 dark:text-surface-300">
                      <p>Attach to an existing Carrier</p>
                      <CarrierTargetPicker
                        ariaLabel="Assign Outbound trunk to a Carrier"
                        disabled={disabled || saving}
                        options={carrierOptions}
                        onSelect={(target) => assignVendor(selectedUnassignedVendor, target)}
                      />
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button type="button" disabled={disabled || saving} onClick={() => assignVendor(selectedUnassignedVendor, 'new')} className="flex h-8 items-center gap-1.5 rounded-lg border border-surface-300 px-3 text-xs font-semibold hover:border-amber-500 dark:border-surface-700"><Plus className="h-3.5 w-3.5" />Create new Carrier</button>
                      <button type="button" disabled={disabled || saving || dirty} onClick={() => openIgnore({
                        recordType: 'vendor_carrier',
                        providerRecordId: selectedUnassignedVendor.providerCarrierId,
                        name: selectedUnassignedVendor.name,
                      })} className="flex h-8 items-center gap-1.5 rounded-lg border border-surface-300 px-3 text-xs font-semibold text-surface-600 hover:border-amber-500 hover:text-amber-700 disabled:opacity-40 dark:border-surface-700 dark:text-surface-300"><Archive className="h-3.5 w-3.5" />Ignore record</button>
                    </div>
                  </div>
                )}

                {view === 'ignored' && selectedIgnored && (
                  <div className="mx-auto max-w-2xl">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-base font-semibold text-surface-950 dark:text-white">{selectedIgnored.name}</h4>
                        <p className="mt-1 text-xs text-surface-500 dark:text-surface-400">{ignoredRecordLabel(selectedIgnored.recordType)}</p>
                      </div>
                      {selectedIgnored.reviewRequired && <span className="rounded-full bg-amber-100 px-2 py-1 text-[0.65rem] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300">Review again</span>}
                    </div>
                    <dl className="mt-5 grid gap-3 rounded-lg border border-surface-200 p-4 text-xs dark:border-surface-800 sm:grid-cols-2">
                      <div><dt className="text-surface-500">Reason</dt><dd className="mt-1 font-semibold">{reasonLabel(selectedIgnored.reason)}</dd></div>
                      <div><dt className="text-surface-500">Ignored by</dt><dd className="mt-1 font-semibold">{selectedIgnored.ignoredBy.username}</dd></div>
                      <div><dt className="text-surface-500">Ignored at</dt><dd className="mt-1 font-semibold">{new Date(selectedIgnored.ignoredAt).toLocaleString()}</dd></div>
                      <div><dt className="text-surface-500">Provider state</dt><dd className="mt-1 font-semibold">{selectedIgnored.present ? 'Still present' : 'No longer discovered'}</dd></div>
                    </dl>
                    {selectedIgnored.reviewRequired && (
                      <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-200">This provider record changed after it was ignored. Review it before deciding whether to restore it.</p>
                    )}
                    <button type="button" disabled={disabled || saving || dirty} onClick={() => void changeDisposition({
                      recordType: selectedIgnored.recordType,
                      providerRecordId: selectedIgnored.providerRecordId,
                      action: 'restore',
                    })} className="mt-4 flex h-8 items-center gap-1.5 rounded-lg border border-surface-300 px-3 text-xs font-semibold hover:border-emerald-500 disabled:opacity-40 dark:border-surface-700"><RotateCcw className="h-3.5 w-3.5" />Restore to review</button>
                  </div>
                )}
              </main>
            </div>
          </section>

          {accountPickerOpen && selectedCarrier && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
              <section role="dialog" aria-modal="true" aria-label="Add Inbound trunks" className="flex max-h-[80vh] w-[min(34rem,94vw)] flex-col overflow-hidden rounded-xl border border-surface-300 bg-white shadow-2xl dark:border-surface-700 dark:bg-surface-900">
                <header className="border-b border-surface-200 px-4 py-3 dark:border-surface-800">
                  <h4 className="text-sm font-semibold">Add Inbound trunks</h4>
                  <p className="mt-1 text-xs text-surface-500">Choose unassigned Inbound trunks for {selectedCarrierName}.</p>
                </header>
                <div className="p-3">
                  <Input value={accountPickerQuery} onChange={(event) => setAccountPickerQuery(event.target.value)} placeholder="Search Inbound trunks or provider companies…" className="h-8 text-xs" />
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-3">
                  {pickerAccounts.map((account) => (
                    <label key={account.providerAccountId} className="flex cursor-pointer items-center gap-2 border-b border-surface-100 py-2.5 text-xs last:border-0 dark:border-surface-800">
                      <input type="checkbox" checked={accountPickerSelection.has(account.providerAccountId)} onChange={(event) => setAccountPickerSelection((current) => {
                        const next = new Set(current);
                        if (event.target.checked) next.add(account.providerAccountId);
                        else next.delete(account.providerAccountId);
                        return next;
                      })} className="accent-amber-500" />
                      <span className="min-w-0">
                        <span title={account.name} className="block break-words">{account.name}</span>
                        <span title={account.company.name} className="block break-words text-[0.68rem] text-surface-500">{account.company.name}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <footer className="flex justify-end gap-2 border-t border-surface-200 px-4 py-3 dark:border-surface-800">
                  <button type="button" onClick={() => setAccountPickerOpen(false)} className="h-8 px-2 text-xs font-semibold text-surface-500">Cancel</button>
                  <button type="button" disabled={accountPickerSelection.size === 0} onClick={() => {
                    for (const accountId of accountPickerSelection) {
                      const account = accounts.find((candidate) => candidate.providerAccountId === accountId);
                      if (account) assignAccount(account, selectedCarrier);
                    }
                    setAccountPickerOpen(false);
                  }} className="h-8 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-surface-950 disabled:opacity-50">Add selected Inbound</button>
                </footer>
              </section>
            </div>
          )}

          {pickerOpen && selectedCarrier && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
              <section role="dialog" aria-modal="true" aria-label="Add Outbound trunks" className="flex max-h-[80vh] w-[min(34rem,94vw)] flex-col overflow-hidden rounded-xl border border-surface-300 bg-white shadow-2xl dark:border-surface-700 dark:bg-surface-900">
                <header className="border-b border-surface-200 px-4 py-3 dark:border-surface-800">
                  <h4 className="text-sm font-semibold">Add Outbound trunks</h4>
                  <p className="mt-1 text-xs text-surface-500">Choose unassigned Outbound trunks for {selectedCarrierName}.</p>
                </header>
                <div className="p-3">
                  <Input value={pickerQuery} onChange={(event) => setPickerQuery(event.target.value)} placeholder="Search Outbound trunks…" className="h-8 text-xs" />
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-3">
                  {pickerVendors.map((vendor) => (
                    <label key={vendor.providerCarrierId} className="flex cursor-pointer items-center gap-2 border-b border-surface-100 py-2.5 text-xs last:border-0 dark:border-surface-800">
                      <input type="checkbox" checked={pickerSelection.has(vendor.providerCarrierId)} onChange={(event) => setPickerSelection((current) => {
                        const next = new Set(current);
                        if (event.target.checked) next.add(vendor.providerCarrierId);
                        else next.delete(vendor.providerCarrierId);
                        return next;
                      })} className="accent-amber-500" />
                      <span title={vendor.name} className="min-w-0 flex-1 break-words">{vendor.name}</span>
                    </label>
                  ))}
                </div>
                <footer className="flex justify-end gap-2 border-t border-surface-200 px-4 py-3 dark:border-surface-800">
                  <button type="button" onClick={() => setPickerOpen(false)} className="h-8 px-2 text-xs font-semibold text-surface-500">Cancel</button>
                  <button type="button" disabled={pickerSelection.size === 0} onClick={() => {
                    for (const vendorId of pickerSelection) {
                      const vendor = vendors.find((candidate) => candidate.providerCarrierId === vendorId);
                      if (vendor) assignVendor(vendor, selectedCarrier);
                    }
                    setPickerOpen(false);
                  }} className="h-8 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-surface-950 disabled:opacity-50">Add selected records</button>
                </footer>
              </section>
            </div>
          )}

          {ignoreTarget && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
              <section role="dialog" aria-modal="true" aria-label={`Ignore ${ignoreTarget.name}`} className="w-[min(28rem,94vw)] rounded-xl border border-surface-300 bg-white p-4 shadow-2xl dark:border-surface-700 dark:bg-surface-900">
                <h4 className="text-sm font-semibold">Ignore {ignoreTarget.name}?</h4>
                <p className="mt-1 text-xs leading-5 text-surface-500 dark:text-surface-400">The provider record remains in the durable history and can be restored later. No Carrier Folder will be created from it.</p>
                <label className="mt-4 block text-xs font-semibold text-surface-600 dark:text-surface-300">
                  Reason
                  <select value={ignoreReason} onChange={(event) => setIgnoreReason(event.target.value as CarrierRecordIgnoreReason | '')} className="mt-1 block h-9 w-full rounded-lg border border-surface-300 bg-white px-2 text-xs text-surface-900 dark:border-surface-700 dark:bg-surface-950 dark:text-surface-100">
                    <option value="">Choose a reason…</option>
                    {IGNORE_REASONS.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
                  </select>
                </label>
                <div className="mt-4 flex justify-end gap-2">
                  <button type="button" onClick={() => {
                    setIgnoreTarget(null);
                    setIgnoreReason('');
                  }} className="h-8 px-2 text-xs font-semibold text-surface-500">Cancel</button>
                  <button type="button" disabled={!ignoreReason || saving} onClick={() => void changeDisposition({
                    recordType: ignoreTarget.recordType,
                    providerRecordId: ignoreTarget.providerRecordId,
                    action: 'ignore',
                    reason: ignoreReason || undefined,
                  })} className="flex h-8 items-center gap-1.5 rounded-lg bg-amber-500 px-3 text-xs font-semibold text-surface-950 disabled:opacity-50">{saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Ignore record</button>
                </div>
              </section>
            </div>
          )}
        </div>
      )}
    </>
  );
}
